import { AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { LoginComponent } from '../../login/components/login/login.component';
import { AuthService } from '../../login/services/auth.service';
import { OAuthService } from '../../login/services/oauth.service';
import { SystemGlobalsService } from '../../system-globals.service';
import {
  AscentLoginFormCopy,
  AscentLoginHeroConfig,
  AscentLoginNotice,
  AscentLoginPageCopy,
  DEFAULT_ASCENT_LOGIN_FORM,
  DEFAULT_ASCENT_LOGIN_HERO,
  DEMO_LOGIN_ERROR,
  DEMO_LOGIN_NOTICES,
  DEMO_LOGIN_RESET,
  mergeLoginCopy
} from './ascent-login.config';

@Component({
  selector: 'app-new-login',
  templateUrl: './new-login.component.html',
  styleUrls: ['./new-login.component.scss', './ascent-login.theme.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewLoginComponent extends LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  previewMode = false;
  heroConfig: AscentLoginHeroConfig = DEFAULT_ASCENT_LOGIN_HERO;
  formCopy: AscentLoginFormCopy = DEFAULT_ASCENT_LOGIN_FORM;
  notices: AscentLoginNotice[] = [];
  private dismissedNoticeIds = new Set<number | string>();

  dialogOpen = false;
  dialogTitle = '';
  dialogCopy = '';

  orgSsoLabel: string | null = null;
  /** From login page copy; ANDed with server ENABLE_SSO + configured providers. */
  showSso = true;

  private demoStateSub: Subscription;
  private contentSub: Subscription;
  private liveNotices: AscentLoginNotice[] = [];
  private demoMessageActive = false;
  private previewOverride = false;
  private readonly onPreviewMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }
    const data = event.data;
    if (!data || data.type !== 'login-copy-preview') {
      return;
    }
    this.previewOverride = true;
    this.applyMergedCopy(data.copy);
  };

  constructor(
    authService: AuthService,
    oauthService: OAuthService,
    dialog: MatDialog,
    cookieService: CookieService,
    globals: SystemGlobalsService,
    http: HttpClient,
    public loginHttp: HttpClient,
    private route: ActivatedRoute
  ) {
    super(authService, oauthService, dialog, cookieService, globals, http);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.orgSsoLabel = this.readOrgSsoLabel();
    this.previewMode = this.route.snapshot.queryParamMap.get('preview') === '1';
    if (this.previewMode) {
      this.isLoggedIn = false;
      this.isInIframe = false;
      window.addEventListener('message', this.onPreviewMessage);
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'login-preview-ready' }, window.location.origin);
      }
    }
    const loginError = this.route.snapshot.queryParamMap.get('error');
    if (loginError === 'token_expired') {
      this.errorMessage = 'Your sign-in link expired. Please enter your credentials again.';
    } else if (loginError === 'login_failed') {
      this.errorMessage = 'Sign-in could not be completed. Please try again.';
    }
    this.demoStateSub = this.route.queryParamMap.subscribe((params) => this.applyDemoState(params));
    this.contentSub = this.loginHttp.get<any>('/users/getLoginContent.json').subscribe((response) => {
      const payload = response && response.data && typeof response.data === 'object'
        ? response.data
        : response;
      this.applyLiveContent(payload);
    });
  }

  ngOnDestroy(): void {
    if (this.demoStateSub) {
      this.demoStateSub.unsubscribe();
    }
    if (this.contentSub) {
      this.contentSub.unsubscribe();
    }
    window.removeEventListener('message', this.onPreviewMessage);
  }

  ngAfterViewInit(): void {
    document.body.style.backgroundColor = '#ffffff';
    document.documentElement.style.backgroundColor = '#ffffff';
  }

  /** Always the Ascent product mark from the login prototype — never legacy Aerostudies/beta assets. */
  get productLogoUrl(): string {
    return '/images/ascent/auth/ascent_product_logo.svg';
  }

  get companySiteUrl(): string {
    return `https://${this.globals.companyName || 'aerostudies'}.com/`;
  }

  get headerPoweredByLogoUrl(): string {
    const system = this.globals.constants?.systemName || 'ascent';
    return `/images/${system}/auth/company_logo.svg`;
  }

  /** Footer Aerostudies / Allantra wordmark SVG. */
  get footerCompanyLogoUrl(): string {
    const system = this.globals.constants?.systemName || 'ascent';
    return `/images/${system}/auth/company_logo.svg`;
  }

  get visibleNotices(): AscentLoginNotice[] {
    return this.notices.filter((n) => !this.dismissedNoticeIds.has(this.noticeKey(n)));
  }

  openDialog(title: string, copy: string): void {
    this.dialogTitle = title;
    this.dialogCopy = copy;
    this.dialogOpen = true;
  }

  closeDialog(): void {
    this.dialogOpen = false;
  }

  onHeroCtaClick(): void {
    this.openDialog(this.heroConfig.eyebrow, this.heroConfig.detail || this.heroConfig.body);
  }

  onNoticeAction(notice: AscentLoginNotice): void {
    if (notice?.detail) {
      this.openDialog(notice.title, notice.detail);
    }
  }

  dismissNotice(notice: AscentLoginNotice): void {
    this.dismissedNoticeIds.add(this.noticeKey(notice));
  }

  onSupportClick(): void {
    this.openDialog(
      'Get help signing in',
      'Use the contact details in your Ascent invitation to reach your organization\'s administrator, or visit https://aerostudies.com/support for assistance.'
    );
  }

  onAdministratorClick(): void {
    this.openDialog(
      'Need access?',
      'Contact your organization\'s training administrator to request an Ascent account.'
    );
  }

  onPrivacyClick(): void {
    window.open('/main/legal/licence/privacy', '_blank');
  }

  onTermsClick(): void {
    window.open('/main/legal/licence/terms', '_blank');
  }

  onForgotPasswordClick(): void {
    this.onForgotPassword();
  }

  onBackToSignIn(): void {
    this.toggleForgotPassword();
    this.resetMessage = '';
    this.hasResetError = false;
  }

  onSubmitLogin(): void {
    this.onLoginSubmit();
  }

  onOrgSsoClick(): void {
    if (this.orgSsoLabel) {
      this.openDialog(
        this.orgSsoLabel,
        'Organization single sign-on is configured for this login page. Complete integration with your identity provider to enable this button.'
      );
    }
  }

  private readOrgSsoLabel(): string | null {
    const ls = new URLSearchParams(window.location.search).get('ls');
    return ls ? `Continue with ${ls}` : null;
  }

  /** Query params preview a notice or form message. Absent params use the live content. */
  private applyDemoState(params: ParamMap): void {
    const noticeKey = params.get('notice');
    const messageKey = params.get('message');

    if (noticeKey) {
      this.dismissedNoticeIds.clear();
      this.notices = noticeKey === 'off'
        ? []
        : [DEMO_LOGIN_NOTICES[noticeKey] || DEMO_LOGIN_NOTICES.release];
    } else {
      this.notices = this.liveNotices;
    }

    if (!messageKey) {
      if (this.demoMessageActive) {
        this.showForgotPassword = false;
        this.resetMessage = '';
        this.hasResetError = false;
        this.errorMessage = '';
        this.demoMessageActive = false;
      }
      return;
    }

    this.demoMessageActive = true;
    if (messageKey === 'reset') {
      this.showForgotPassword = true;
      this.hasResetError = false;
      this.resetMessage = DEMO_LOGIN_RESET;
      this.errorMessage = '';
      return;
    }

    this.showForgotPassword = false;
    this.resetMessage = '';
    this.hasResetError = false;
    this.errorMessage = messageKey === 'off' ? '' : DEMO_LOGIN_ERROR;
  }

  private applyLiveContent(payload: {
    notices?: AscentLoginNotice[] | null;
    notice?: AscentLoginNotice | null;
    copy: AscentLoginPageCopy;
  }): void {
    if (!this.previewOverride) {
      this.applyMergedCopy(payload && payload.copy);
    }

    if (payload && Array.isArray(payload.notices)) {
      this.liveNotices = payload.notices;
    } else if (payload && payload.notice) {
      this.liveNotices = [payload.notice];
    } else {
      this.liveNotices = [];
    }

    const noticeKey = this.route.snapshot.queryParamMap.get('notice');
    if (!noticeKey) {
      this.notices = this.liveNotices;
    }
  }

  private applyMergedCopy(copy: Partial<AscentLoginPageCopy>): void {
    const merged = mergeLoginCopy(copy);
    this.heroConfig = merged.hero;
    this.formCopy = merged.form;
    this.showSso = merged.showSso;
  }

  private noticeKey(notice: AscentLoginNotice): number | string {
    if (notice.id != null) {
      return notice.id;
    }
    return `${notice.kind}:${notice.title}:${notice.body}`;
  }
}
